
// Safe highlight function that returns structured data instead of HTML strings
export function highlightSearchTerm(text: string, searchTerm: string): Array<{text: string, highlighted: boolean}> {
  if (!searchTerm.trim() || !text) return [{text, highlighted: false}];
  
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  const parts: Array<{text: string, highlighted: boolean}> = [];
  let lastIndex = 0;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push({text: text.slice(lastIndex, match.index), highlighted: false});
    }
    // Add highlighted match
    parts.push({text: match[0], highlighted: true});
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({text: text.slice(lastIndex), highlighted: false});
  }
  
  return parts;
}

export function truncateWithHighlight(text: string, searchTerm: string, maxLength: number = 90, isMobile: boolean = false): {parts: Array<{text: string, highlighted: boolean}>, needsHighlight: boolean} {
  if (!text || !searchTerm.trim()) return {parts: [{text, highlighted: false}], needsHighlight: false};
  
  const lowerText = text.toLowerCase();
  const lowerTerm = searchTerm.toLowerCase();
  const termIndex = lowerText.indexOf(lowerTerm);
  
  if (termIndex === -1) return {parts: [{text, highlighted: false}], needsHighlight: false};
  
  // Se o texto for menor que maxLength, retorna o texto completo
  if (text.length <= maxLength) return {parts: highlightSearchTerm(text, searchTerm), needsHighlight: true};
  
  const termLength = searchTerm.length;
  
  // Adjust context based on device - mobile gets less context to prioritize showing the search term
  let contextBefore: number;
  let contextAfter: number;
  
  if (isMobile) {
    // On mobile, use minimal context to ensure search term is visible
    contextBefore = Math.min(8, Math.floor((maxLength - termLength) / 3)); // Less context before
    contextAfter = maxLength - termLength - contextBefore;
  } else {
    // Desktop gets balanced context
    contextBefore = Math.floor((maxLength - termLength) / 2);
    contextAfter = maxLength - termLength - contextBefore;
  }
  
  // Calcular posições ideais
  let start = Math.max(0, termIndex - contextBefore);
  let end = Math.min(text.length, termIndex + termLength + contextAfter);
  
  // Ajustar se não conseguir centralizar perfeitamente
  if (end - start < maxLength && end < text.length) {
    end = Math.min(text.length, start + maxLength);
  }
  if (end - start < maxLength && start > 0) {
    start = Math.max(0, end - maxLength);
  }
  
  // Tentar quebrar em palavras completas quando possível
  let excerpt = text.substring(start, end);
  
  // Ajustar início para não cortar palavras (a menos que seja necessário para mostrar o termo)
  if (start > 0 && text[start] !== ' ' && text[start - 1] !== ' ') {
    const spaceIndex = excerpt.indexOf(' ');
    const maxWordAdjustment = isMobile ? 10 : 20; // Less word adjustment on mobile
    if (spaceIndex > 0 && spaceIndex < maxWordAdjustment) {
      excerpt = excerpt.substring(spaceIndex + 1);
      start += spaceIndex + 1;
    }
  }
  
  // Ajustar fim para não cortar palavras
  if (end < text.length && text[end] !== ' ' && text[end - 1] !== ' ') {
    const lastSpaceIndex = excerpt.lastIndexOf(' ');
    const maxWordAdjustment = isMobile ? 10 : 20; // Less word adjustment on mobile
    if (lastSpaceIndex > excerpt.length - maxWordAdjustment) {
      excerpt = excerpt.substring(0, lastSpaceIndex);
      end = start + lastSpaceIndex;
    }
  }
  
  // Adicionar ellipsis quando necessário
  if (start > 0) excerpt = '...' + excerpt;
  if (end < text.length) excerpt = excerpt + '...';
  
  return {parts: highlightSearchTerm(excerpt, searchTerm), needsHighlight: true};
}
